import {
  SITE_ASSET_DIRS,
  SITE_ASSET_FILES,
  SITE_ASSET_IMAGES,
  SITE_ASSET_VIDEOS,
} from "./siteMediaManifest";

/**
 * 사이트 미디어 주소 — 모든 사진·영상·장면 데이터 주소는 여기서만 만든다(site-media-delivery).
 * 기본 주소는 설정값 하나(`NEXT_PUBLIC_SITE_MEDIA_BASE`)로 바꾸고, 없으면 같은 출처의 `/site-media`(저장소의 `public/site-media/`)를 쓴다.
 * 파일 이름은 파이프라인이 붙인 내용 해시를 담아 1년 캐시해도 안전하다.
 */
const SAME_ORIGIN_MEDIA_BASE = "/site-media";

const mediaBase = () =>
  (process.env.NEXT_PUBLIC_SITE_MEDIA_BASE || SAME_ORIGIN_MEDIA_BASE).replace(
    /\/+$/,
    ""
  );

export const siteMediaUrl = (path: string) => `${mediaBase()}/${path}`;

export interface SiteImageEntry {
  /** 폴더 */
  d: string;
  /** 묶음 해시 */
  h: string;
  /** 너비 변형(오름차순) */
  w: readonly number[];
  /** 가로/세로 */
  r: number;
  /** 대표 색 — 받는 동안 바탕 */
  c: string;
  /** 흐린 미리보기 data URI(첫 화면 그림만) */
  q?: string;
}

export interface SiteVideoEntry {
  d: string;
  h: string;
  /** 렌디션 너비(오름차순) */
  v: readonly number[];
  /** 포스터 너비(오름차순) */
  p: readonly number[];
  q?: string;
}

export type SiteImageName = keyof typeof SITE_ASSET_IMAGES;
export type SiteVideoName = keyof typeof SITE_ASSET_VIDEOS;
export type SiteFileKey = keyof typeof SITE_ASSET_FILES;
export type SiteDirKey = keyof typeof SITE_ASSET_DIRS;

const IMAGES: Record<SiteImageName, SiteImageEntry> = SITE_ASSET_IMAGES;
const VIDEOS: Record<SiteVideoName, SiteVideoEntry> = SITE_ASSET_VIDEOS;
const FILES: Record<SiteFileKey, string> = SITE_ASSET_FILES;
const DIRS: Readonly<Record<string, { d: string; q?: string }>> =
  SITE_ASSET_DIRS;

export const siteImageEntry = (name: SiteImageName): SiteImageEntry =>
  IMAGES[name];

export const siteImageUrl = (name: SiteImageName, width: number) => {
  const entry = IMAGES[name];
  return siteMediaUrl(`${entry.d}/${name}.${entry.h}-${width}.webp`);
};

export const siteImageSrcSet = (name: SiteImageName) =>
  IMAGES[name].w
    .map((width) => `${siteImageUrl(name, width)} ${width}w`)
    .join(", ");

export const siteFileUrl = (key: SiteFileKey) => siteMediaUrl(FILES[key]);

/** 폴더 묶음 안 파일 주소(첫 장면 프레임·장면 데이터) */
export const siteDirUrl = (key: SiteDirKey, innerPath: string) =>
  siteMediaUrl(`${DIRS[key].d}/${innerPath}`);

export const siteDirPreview = (key: SiteDirKey) => DIRS[key].q;

export const siteVideoEntry = (name: SiteVideoName): SiteVideoEntry =>
  VIDEOS[name];

export const siteVideoUrl = (name: SiteVideoName, width: number) => {
  const entry = VIDEOS[name];
  return siteMediaUrl(`${entry.d}/${name}.${entry.h}-${width}.mp4`);
};

export const siteVideoPosterUrl = (name: SiteVideoName, width: number) => {
  const entry = VIDEOS[name];
  return siteMediaUrl(`${entry.d}/${name}.${entry.h}-${width}.webp`);
};
